import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, X, Plus } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUrlState } from "@/hooks/use-url-state";
import { apiRequest } from "@/lib/queryClient";
import { ComplianceLayout } from "./compliance-layout";
import { CreditRepModal } from "@/components/modals/credit-rep-modal";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreditRepresentative {
  id: string;
  entityName: string;
  crNumber: string | null;
  appointmentType: string;
  entityType: string | null;
  crStatus: string;
  complianceStatus: string;
  appointmentDate: string | null;
  cessationDate: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CR_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-green-100 text-green-700 border-green-300" },
  suspended: { label: "Suspended", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  terminated: { label: "Terminated", className: "bg-red-100 text-red-700 border-red-300" },
  pending: { label: "Pending", className: "bg-slate-100 text-slate-700 border-slate-300" },
};

const COMPLIANCE_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  compliant: { label: "Compliant", className: "bg-green-100 text-green-700 border-green-300" },
  non_compliant: { label: "Non-Compliant", className: "bg-red-100 text-red-700 border-red-300" },
  under_review: { label: "Under Review", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  remediation_required: { label: "Remediation Required", className: "bg-orange-100 text-orange-700 border-orange-300" },
};

const STATUS_OPTIONS = [
  { value: "none", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "terminated", label: "Terminated" },
];

const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  full: "Full",
  limited: "Limited",
  provisional: "Provisional",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Badge components
// ---------------------------------------------------------------------------

function CrStatusBadge({ status }: { status: string }) {
  const config = CR_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`cr-status-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

function ComplianceStatusBadge({ status }: { status: string }) {
  const config = COMPLIANCE_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`compliance-status-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function CreditRepsListContent() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [search, setSearch] = useUrlState("search", "", { debounceMs: 300 });
  const [status, setStatus] = useUrlState("status", "none");

  // Build query params
  const queryParams = new URLSearchParams();
  if (search) queryParams.set("search", search);
  if (status !== "none") queryParams.set("status", status);

  const { data, isLoading } = useQuery<CreditRepresentative[]>({
    queryKey: ["/api/compliance/credit-reps", search, status],
    queryFn: async () => {
      const url = `/api/compliance/credit-reps${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const creditReps = Array.isArray(data) ? data : [];

  const hasActiveFilters = search !== "" || status !== "none";

  const clearSearch = () => setSearch("");

  const handleRowClick = (cr: CreditRepresentative) => {
    setLocation(`/compliance/credit-reps/${cr.id}`);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/compliance/credit-reps/${id}`
      );
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to delete credit representative");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/credit-reps"] });
      toast({ title: "Credit representative deleted" });
      setDeletingId(null);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
      setDeletingId(null);
    },
  });

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Credit Representatives</h1>
          <p className="text-sm text-muted-foreground">
            ASIC credit representative register
          </p>
        </div>
        <Button
          data-testid="button-add-cr"
          onClick={() => setIsAddModalOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Credit Representative
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or CR number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-9"
              data-testid="input-search-cr"
            />
            {search && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
                data-testid="button-clear-search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              className="w-[175px]"
              data-testid="select-filter-status"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setStatus("none");
              }}
              className="text-muted-foreground"
            >
              <X className="mr-1 h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="text-xs text-muted-foreground mt-2">
            Showing {creditReps.length} credit representative{creditReps.length !== 1 ? "s" : ""}
          </div>
        )}
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>CR Number</TableHead>
              <TableHead>Appointment Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Compliance</TableHead>
              <TableHead>Appointment Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : creditReps.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  <div className="space-y-2">
                    <p>No Credit Representatives found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-add-cr-empty"
                      onClick={() => setIsAddModalOpen(true)}
                    >
                      Add Credit Representative
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              creditReps.map((cr) => (
                <TableRow
                  key={cr.id}
                  data-testid={`row-cr-${cr.id}`}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(cr)}
                >
                  <TableCell className="font-medium">{cr.entityName}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {cr.crNumber ?? "-"}
                  </TableCell>
                  <TableCell>
                    {APPOINTMENT_TYPE_LABELS[cr.appointmentType] ?? cr.appointmentType}
                  </TableCell>
                  <TableCell>
                    <CrStatusBadge status={cr.crStatus} />
                  </TableCell>
                  <TableCell>
                    <ComplianceStatusBadge status={cr.complianceStatus} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(cr.appointmentDate)}
                  </TableCell>
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    className="whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`button-view-cr-${cr.id}`}
                        onClick={() => setLocation(`/compliance/credit-reps/${cr.id}`)}
                      >
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`button-delete-cr-${cr.id}`}
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeletingId(cr.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add CR Modal */}
      <CreditRepModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        onSuccess={(id) => setLocation(`/compliance/credit-reps/${id}`)}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingId}
        onOpenChange={(open) => {
          if (!open) setDeletingId(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credit Representative</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this credit representative? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deletingId) deleteMutation.mutate(deletingId);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function CreditRepsPage() {
  return (
    <ComplianceLayout>
      <CreditRepsListContent />
    </ComplianceLayout>
  );
}
